import _ from "lodash";
import {getMappedTree, getNodes} from "./tree";

const AUTO_ANNOTATION_KEYS = [
  "categorySchemesOrder",
  "layoutCategorySchemesOrder",
  "conceptSchemesOrder",
  "categorisationsOrder",
  "codelistsOrder",
  "associatedCube",
  "changed",
  "customDSD",
  "ddbDataflow",
  "haveMetadata",
  "metadataset",
  "customIsPresentational",
  "linkedDataflowNode",
  "lastUpdate",
  "AnnotationServiceURL",
  "AnnotationStructureURL",
  "dataflowCatalogType",
  "ddbContentConstraint"
];

export const AUTO_ANNOTATION_NONPRODUCTIONDATAFLOW_TYPE = "nonproductiondataflow";

export const artefactType = {
  'concept-schemes': 'ConceptSchemes',
  'codelists': 'CodeLists',
  'data-structure-definitions': 'DataStructureDefinitions',
  'dataflows': 'DataFlows',
  'category-schemes': 'CategorySchemes',
  'hierarchical-codelists': 'HierarchicalCodelists',
  'agency-schemes': 'AgencySchemes',
  'content-constraints': 'ContentConstraints',
  'msds': 'MetadataStructureDefinitions',
};

export const updateItemOrder = (item, order, lang) => {
  item.order = {
    ...item.order,
    [lang]: order
  };
};

export const getUpdatedAndOrderedItemTree = (itemsTree, childrenKey, item, lang) => {
  const root = [{
    isRoot: true,
    [childrenKey]: itemsTree
  }];

  const itemCount = getNodes(itemsTree, childrenKey, () => true).length;

  return getMappedTree(root, childrenKey, parentItem => {
    if (parentItem.id === item.parent || (!parentItem.id && !item.parent)) {
      const node = parentItem[childrenKey].find(child => child.id === item.id);
      const newItem = _.cloneDeep(item);

      const oldOrder = node
        ? node.order[lang]
        : null;
      const newOrder = (item.order[lang] && item.order[lang] > 0)
        ? item.order[lang]
        : null;
      const order = newOrder
        ? (!oldOrder || newOrder < oldOrder)
          ? newOrder - 0.1
          : newOrder + 0.1
        : Number.MAX_VALUE;
      updateItemOrder(newItem, order, lang);

      const firstItem = itemsTree[0] || null;
      if (firstItem && firstItem.order) {
        Object.keys(firstItem.order).forEach(orderLang => {
          if (orderLang !== lang) {
            updateItemOrder(newItem, itemCount + 1, orderLang);
          }
        });
      }

      if (node) {
        parentItem[childrenKey] = parentItem[childrenKey].filter(child => child.id !== item.id);
      }
      parentItem[childrenKey].push(newItem);

      parentItem[childrenKey].sort((a, b) => itemsOrderCompare(a, b, lang));

      return parentItem;

    } else {
      parentItem[childrenKey] = parentItem[childrenKey].filter(child => child.id !== item.id);
    }

    return parentItem;

  })[0][childrenKey];
}

export const itemsOrderCompare = (a, b, lang) => a.order[lang] - b.order[lang];

export const isAutoAnnotation = (annotation, annotationsConfig) => {
  const autoAnnotationTypes = (_.flatMap(_.pick(annotationsConfig, AUTO_ANNOTATION_KEYS)))    
    .map(annot => (annot.type || "").toLowerCase())
    .concat(AUTO_ANNOTATION_NONPRODUCTIONDATAFLOW_TYPE);

  return annotation.type && autoAnnotationTypes.includes(annotation.type.toLowerCase())
};

export const getFilteredAnnotations = (annotations, annotationsConfig, customIsAutoAnnotation) => {

  return (annotations && annotations.length > 0)
    ? annotations.filter(annotation => customIsAutoAnnotation
      ? !customIsAutoAnnotation(annotation, annotationsConfig)
      : !isAutoAnnotation(annotation, annotationsConfig)
    )
    : []
};

export const getAutoAnnotations = (annotations, annotationsConfig, customIsAutoAnnotation) => {

  return (annotations && annotations.length > 0)
    ? annotations.filter(annotation => customIsAutoAnnotation
      ? customIsAutoAnnotation(annotation, annotationsConfig)
      : isAutoAnnotation(annotation, annotationsConfig)
    )
    : []
};

export const normalizeDsdComponentsOrder = (components, componentId, prevComponentOrder = null) =>
  components
    ? components
      .map(comp => ({
        ...comp,
        order: comp.order ? Number(comp.order) : null
      }))
      .sort((a, b) => {
        if (a.order === b.order) {
          if (a.id === componentId && prevComponentOrder !== null) {
            return a.order - prevComponentOrder;
          } else if (b.id === componentId && prevComponentOrder !== null) {
            return prevComponentOrder - b.order;
          } else {
            return a.id - b.id;
          }
        } else if (a.order !== null && b.order === null) {
          return -1;
        } else if (a.order === null && b.order !== null) {
          return 1;
        } else {
          return a.order - b.order;
        }
      })
      .map((comp, idx) => ({
        ...comp,
        order: idx + 1
      }))
    : null;

export const existsComponentWithId = (dsd, id) =>
  Boolean([
    dsd.primaryMeasure,
    ...(dsd.dimensions || []),
    ...(dsd.groups || []),
    ...(dsd.attributes || [])
  ].find(component => component.id === id));

export const getItemOrder = (item, orderAnnotationType) => {
  const orderFromAnnotation = ((item.annotations || []).find(annot => annot.type && annot.type === orderAnnotationType) || {}).texts;

  if (!orderFromAnnotation || _.isEmpty(orderFromAnnotation)) {
    return undefined;
  }

  const order = {};
  Object.keys(orderFromAnnotation).forEach(key => {
    if (orderFromAnnotation[key].match(/^([1-9]\d*)$/g)) {
      order[key] = Number(orderFromAnnotation[key]);
    }
  });

  return order;
};

export const getSQLItemFromItem = (item, lang, autoSave, itemsOrderAnnotationType, orderLangs, itemCount) => {

  let annotations = (item.annotations && item.annotations.length > 0)
    ? item.annotations
      .filter(annot => !_.isEmpty(annot) && annot.type !== itemsOrderAnnotationType)
      .map(annot => ({
        id: annot.id ? annot.id : undefined,
        title: annot.title ? annot.title : undefined,
        type: annot.type ? annot.type : undefined,
        texts: annot.text ? _.pickBy(annot.text, val => val.length > 0) : undefined // fix after sdmx-source update 1.17
      }))
    : [];
  const autoAnnotations = (item.autoAnnotations && item.autoAnnotations.length > 0)
    ? item.autoAnnotations
      .filter(annot => !_.isEmpty(annot) && annot.type !== itemsOrderAnnotationType)
      .map(annot => ({
        id: annot.id ? annot.id : undefined,
        title: annot.title ? annot.title : undefined,
        type: annot.type ? annot.type : undefined,
        texts: annot.text ? _.pickBy(annot.text, val => val.length > 0) : undefined // fix after sdmx-source update 1.17
      }))
    : [];
  annotations = annotations.concat(autoAnnotations);

  const name = _.pickBy(item.name, val => val.length > 0);
  const description = _.pickBy(item.description, val => val.length > 0);
  let order = {...item.order};
  orderLangs.forEach(orderLang => order[orderLang] = itemCount + 1);
  order = _.pickBy(order, val => val && val > 0);

  return ({
    itemCode: item.id,
    parent: item.parent ? item.parent : undefined,
    name: (name && name[lang]) ? name[lang] : undefined,
    names: name,
    desc: (description && description[lang]) ? description[lang] : undefined,
    descs: !_.isEmpty(description) ? description : undefined,
    treePosition: (order && order[lang]) ? order[lang] : undefined,
    order: !_.isEmpty(order) ? order : undefined,
    annotations: annotations.length > 0 ? annotations : undefined,
    autoSave: autoSave
  })
};

export const getOrderedChildren = (children, parentId, lang, itemsOrderAnnotationType) => {
  const orderedChilds = [];
  const childs = [];

  children && children.forEach(child => {
    let order = undefined;

    if (child.order) {
      order = child.order;

    } else {
      const orderFromAnnotation = ((child.autoAnnotations || []).find(annot => annot.type && annot.type === itemsOrderAnnotationType) || {}).text;
      if (orderFromAnnotation && !_.isEmpty(orderFromAnnotation)) {
        order = {};
        Object.keys(orderFromAnnotation).forEach(key => {
          if (orderFromAnnotation[key].match(/^([1-9]\d*)$/g)) {
            order[key] = Number(orderFromAnnotation[key]);
          }
        });
      }
    }

    if (order && order[lang]) {
      orderedChilds.push({
        ...child,
        order: order,
        parent: parentId
      })
    } else {
      childs.push({
        ...child,
        order: order,
        parent: parentId
      })
    }
  });

  orderedChilds.sort((a, b) => itemsOrderCompare(a, b, lang));

  return orderedChilds.concat(childs);
};

export const getLocalizedArtefactName = (artefact, lang) => {
  artefact.name = Object.hasOwn(artefact.name, lang) ? artefact.name[lang] : artefact.name[Object.keys(artefact.name)[0]];
  return artefact;
}